/**
 * `/templates/{id}/edit` — screen 20.
 *
 * Four panes, per the mockup: **Structure · Constraints · Localization · Versions**. Structure is
 * the three-column layout — section rail, item table, aside.
 *
 * ## The fact the whole screen is arranged around
 *
 * > A published version is frozen. "Editing a template" means *starting the next version*.
 *
 * That is not a restriction to work around; it is the guarantee that a parish which planned from v3
 * keeps exactly what it planned from, whatever the diocese publishes next. So this screen never
 * offers to edit a published version — it offers to open a draft, which starts as a copy. When
 * there is no draft, the panes are read-only and say why.
 *
 * ## What a Template may not do
 *
 * A Template defines the *shape* of planning. It cannot approve anything, cannot grant anyone
 * access, and cannot decide what a particular celebration chooses. The aside says so, because the
 * authoring surface is exactly where somebody starts imagining it can.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  CONSTRAINT_KINDS,
  ITEM_TYPES,
  type ConstraintKind,
  type ItemType,
  type TemplateDefinition,
  type TemplateItem,
  type TemplateOption,
  type TemplateSection,
} from '@pius/contracts';

import type { ApiError } from '../../api/client';
import { useAuth } from '../auth/useAuth';
import { Notice } from '../../components/primitives';
import {
  duplicateTemplate,
  fetchLocalizations,
  fetchPublishPreview,
  fetchTemplate,
  publishDraft,
  saveDraft,
  saveLocalization,
  startDraft,
  type TemplateDetail,
  type TemplateVersionView,
} from './api';
import { BulkItemModal } from './BulkItemModal';
import { BulkOptionModal } from './BulkOptionModal';
import { ImportDraftModal } from './ImportDraftModal';
import { slugifyKey } from './bulkOptionParser';
import { createTemplateBundle, downloadFile } from './templateBundle';
import './template-editor.css';

type Pane = 'structure' | 'constraints' | 'localization' | 'versions';

const PANES: { value: Pane; key: string }[] = [
  { value: 'structure', key: 'templates.pane_structure' },
  { value: 'constraints', key: 'templates.pane_constraints' },
  { value: 'localization', key: 'templates.pane_translations' },
  { value: 'versions', key: 'templates.pane_versions' },
];

const EMPTY: TemplateDefinition = { primary_language: 'en', sections: [], roles: [] };

export function TemplateEditorScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/templates/$id/edit' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const { account } = useAuth();

  const [pane, setPane] = useState<Pane>('structure');
  const [publishing, setPublishing] = useState(false);
  const [draftImportOpen, setDraftImportOpen] = useState(false);

  const template = useQuery({ queryKey: ['template', id], queryFn: () => fetchTemplate(id) });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['template', id] });
    await queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  const detail = template.data;
  const draft = detail?.versions.find((v) => !v.is_published);
  const published = detail?.versions.filter((v) => v.is_published) ?? [];
  const latest = published[published.length - 1];

  const open = useMutation({
    // With no published version there is nothing to copy, so a first draft needs a starting
    // definition. The server refuses an empty one rather than inventing a shape.
    mutationFn: () => startDraft(id, latest ? undefined : EMPTY),
    onSuccess: refresh,
  });

  const duplicate = useMutation({
    mutationFn: () => duplicateTemplate(id),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      await navigate({ to: '/templates/$id/edit', params: { id: created.id } });
    },
  });

  const error = [template.error, open.error, duplicate.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  if (template.isPending) {
    return (
      <div className="tpl-ed">
        <p className="tpl-ed__fine">{t('templates.loading')}</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="tpl-ed">
        {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
        <p className="tpl-ed__fine">
          <Link to="/templates">{t('templates.back_to_templates')}</Link>
        </p>
      </div>
    );
  }

  const handleExportBundle = () => {
    if (!detail) return;
    const bundle = createTemplateBundle(detail);
    const json = JSON.stringify(bundle, null, 2);
    const filename = `${slugifyKey(detail.name) || 'template'}.pius.json`;
    downloadFile(filename, json, 'application/json');
  };

  return (
    <div className="tpl-ed">
      <header className="tpl-ed__head">
        <div>
          <p className="tpl-ed__eyebrow">
            {draft
              ? t('templates.draft_eyebrow', { version: draft.version_number })
              : t('templates.no_draft_open')}
          </p>
          <h1 className="tpl-ed__title">{detail.name}</h1>
          <p className="tpl-ed__lede">{t('templates.editor_lede')}</p>
        </div>

        <div className="tpl-ed__head-acts">
          <button
            type="button"
            className="tpl-btn tpl-btn--ghost"
            onClick={handleExportBundle}
            title={t('templates.export_bundle_tooltip')}
          >
            {t('templates.export')}
          </button>
          {draft && detail.may_edit ? (
            <button
              type="button"
              className="tpl-btn tpl-btn--ghost"
              onClick={() => setDraftImportOpen(true)}
              title={t('templates.import_draft_modal_lede')}
            >
              📥 {t('templates.import_draft_btn')}
            </button>
          ) : null}
          {draft ? (
            <button type="button" className="tpl-ed__btn" onClick={() => setPublishing(true)}>
              {t('templates.publish_v', { version: draft.version_number })}
            </button>
          ) : detail.may_edit ? (
            <button
              type="button"
              className="tpl-ed__btn"
              disabled={open.isPending}
              onClick={() => open.mutate()}
            >
              {open.isPending
                ? t('templates.opening')
                : latest
                  ? t('templates.start_next_version')
                  : t('templates.start_draft')}
            </button>
          ) : account ? (
            <button
              type="button"
              className="tpl-ed__btn"
              disabled={duplicate.isPending}
              onClick={() => duplicate.mutate()}
            >
              {duplicate.isPending ? t('templates.duplicating') : t('templates.duplicate_template')}
            </button>
          ) : null}
          <Link className="tpl-ed__link" to="/templates">
            {t('nav.templates')} →
          </Link>
        </div>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      {!detail.may_edit ? (
        <div className="tpl-ed__canonical-banner">
          <div className="tpl-ed__canonical-text">
            <strong>{t('templates.standard_template_badge')}</strong>
            <p>{t('templates.canonical_banner_desc')}</p>
          </div>
          {account ? (
            <button
              type="button"
              className="tpl-btn tpl-btn--primary"
              disabled={duplicate.isPending}
              onClick={() => duplicate.mutate()}
            >
              {duplicate.isPending ? t('templates.duplicating') : t('templates.duplicate_to_edit')}
            </button>
          ) : null}
        </div>
      ) : null}

      <nav className="tpl-ed__panes">
        {PANES.map((entry) => (
          <button
            key={entry.value}
            type="button"
            className={`tpl-ed__pane${pane === entry.value ? ' tpl-ed__pane--on' : ''}`}
            onClick={() => setPane(entry.value)}
          >
            {t(entry.key)}
          </button>
        ))}
      </nav>

      {/* Read-only rather than absent when nothing is open. Somebody looking at a diocesan template
          they cannot edit should still be able to read its structure. */}
      {!draft ? (
        <p className="tpl-ed__notice">
          {published.length === 0
            ? t('templates.nothing_drafted')
            : `${t('templates.showing_v_frozen', { version: latest?.version_number ?? '' })}${
                detail.may_edit ? ` ${t('templates.start_next_version_to_change')}` : ''
              }`}
        </p>
      ) : null}

      {pane === 'structure' ? (
        <StructurePane detail={detail} draft={draft} onSaved={refresh} />
      ) : pane === 'constraints' ? (
        <ConstraintsPane draft={draft} version={latest} onSaved={refresh} templateId={id} />
      ) : pane === 'localization' ? (
        <LocalizationPane templateId={id} version={draft ?? latest} />
      ) : (
        <VersionsPane detail={detail} />
      )}

      {publishing && draft ? (
        <PublishModal
          templateId={id}
          versionNumber={draft.version_number}
          onClose={() => setPublishing(false)}
          onPublished={async () => {
            setPublishing(false);
            await refresh();
          }}
        />
      ) : null}

      {draft && detail.may_edit ? (
        <ImportDraftModal
          templateId={id}
          draftId={draft.id}
          isOpen={draftImportOpen}
          onClose={() => setDraftImportOpen(false)}
          onImported={async () => {
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

// ── structure ───────────────────────────────────────────────────────────────

/**
 * Section rail · item table · aside, as the mockup lays it out.
 *
 * Edits are held locally and saved explicitly. Autosaving here would be wrong in a way it is not on
 * the participant view: restructuring a template is deliberate work with intermediate states that
 * are meant to be invalid, and a save on every keystroke would reject half of them.
 */
function StructurePane({
  detail,
  draft,
  onSaved,
}: {
  detail: TemplateDetail;
  draft: TemplateVersionView | undefined;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const source =
    draft?.definition ?? detail.versions[detail.versions.length - 1]?.definition ?? EMPTY;

  const [definition, setDefinition] = useState<TemplateDefinition>(source);
  const [selected, setSelected] = useState(0);
  const [bulkItemsOpen, setBulkItemsOpen] = useState(false);

  // Re-seeded when the draft changes underneath — opening a new draft, or a save landing.
  useEffect(() => {
    setDefinition(source);
    setSelected(0);
  }, [draft?.id, source]);

  const save = useMutation({
    mutationFn: () => saveDraft(detail.id, definition),
    onSuccess: onSaved,
  });

  const editable = draft !== undefined;
  const section = definition.sections[selected];
  const error = save.error instanceof Error ? (save.error as ApiError) : undefined;

  const update = (next: TemplateDefinition): void => setDefinition(next);

  const updateSection = (patch: Partial<TemplateSection>): void => {
    if (!section) return;
    const sections = [...definition.sections];
    sections[selected] = { ...section, ...patch };
    update({ ...definition, sections });
  };

  const updateItem = (index: number, patch: Partial<TemplateItem>): void => {
    if (!section) return;
    const items = [...section.items];
    const existing = items[index];
    if (!existing) return;
    items[index] = { ...existing, ...patch };
    updateSection({ items });
  };

  return (
    <div className="tpl-ed__three">
      {/* rail */}
      <aside className="tpl-ed__rail">
        <p className="tpl-ed__eyebrow">{t('templates.sections')}</p>
        <ul className="tpl-ed__rail-list">
          {definition.sections.map((entry, index) => (
            <li key={entry.key}>
              <button
                type="button"
                className={`tpl-ed__rail-item${index === selected ? ' tpl-ed__rail-item--on' : ''}`}
                onClick={() => setSelected(index)}
              >
                <span className="tpl-ed__rail-label">{entry.label || entry.key}</span>
                <span className="tpl-ed__rail-count">{entry.items.length}</span>
              </button>
            </li>
          ))}
        </ul>

        {editable ? (
          <button
            type="button"
            className="tpl-ed__ghost"
            onClick={() => {
              const ordinal = definition.sections.length + 1;
              update({
                ...definition,
                sections: [
                  ...definition.sections,
                  {
                    key: `section_${ordinal}`,
                    label: t('templates.new_section_default'),
                    ordinal,
                    items: [],
                  },
                ],
              });
              setSelected(definition.sections.length);
            }}
          >
            {t('templates.add_section')}
          </button>
        ) : null}
      </aside>

      {/* items */}
      <div className="tpl-ed__main">
        {!section ? (
          <section className="tpl-ed__card">
            <p className="tpl-ed__body">{t('templates.no_sections_body')}</p>
          </section>
        ) : (
          <section className="tpl-ed__card">
            <div className="tpl-ed__field-row">
              <label className="tpl-ed__field">
                <span className="tpl-ed__field-label">{t('templates.col_section')}</span>
                <input
                  value={section.label}
                  disabled={!editable}
                  onChange={(e) => updateSection({ label: e.target.value })}
                />
              </label>
              <label className="tpl-ed__field tpl-ed__field--key">
                <span className="tpl-ed__field-label">{t('templates.col_key')}</span>
                <input
                  className="tpl-ed__mono"
                  value={section.key}
                  disabled={!editable}
                  onChange={(e) => updateSection({ key: e.target.value })}
                />
                {/* The stable identifier a Liturgy's workspace copies. Renaming it in a new version
                    is a different item, not a renamed one. */}
                <span className="tpl-ed__hint">{t('templates.stable_identifier')}</span>
              </label>
            </div>

            <ul className="tpl-ed__items">
              {section.items.map((item, index) => (
                <li key={item.key} className="tpl-ed__item">
                  <div className="tpl-ed__item-head">
                    <input
                      className="tpl-ed__item-label"
                      value={item.label}
                      disabled={!editable}
                      onChange={(e) => updateItem(index, { label: e.target.value })}
                    />
                    <select
                      value={item.item_type}
                      disabled={!editable}
                      onChange={(e) => updateItem(index, { item_type: e.target.value as ItemType })}
                    >
                      {ITEM_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {t(`templates.type_${type}`, { defaultValue: type.replace(/_/g, ' ') })}
                        </option>
                      ))}
                    </select>
                    {editable ? (
                      <button
                        type="button"
                        className="tpl-ed__ghost"
                        onClick={() =>
                          updateSection({ items: section.items.filter((_, i) => i !== index) })
                        }
                      >
                        {t('templates.remove')}
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>

                  <input
                    className="tpl-ed__mono tpl-ed__item-key"
                    value={item.key}
                    disabled={!editable}
                    onChange={(e) => updateItem(index, { key: e.target.value })}
                  />

                  <OptionEditor
                    item={item}
                    editable={editable}
                    onChange={(options) => updateItem(index, { options })}
                  />
                </li>
              ))}
            </ul>

            {editable ? (
              <div
                style={{
                  display: 'flex',
                  gap: '14px',
                  alignItems: 'center',
                  marginTop: '14px',
                }}
              >
                <button
                  type="button"
                  className="tpl-ed__ghost"
                  onClick={() => {
                    const ordinal = section.items.length + 1;
                    updateSection({
                      items: [
                        ...section.items,
                        {
                          key: `item_${ordinal}`,
                          label: t('templates.new_item_default'),
                          item_type: 'single_option',
                          ordinal,
                          options: [],
                        },
                      ],
                    });
                  }}
                >
                  + {t('templates.add_item')}
                </button>
                <button
                  type="button"
                  className="tpl-ed__ghost"
                  onClick={() => setBulkItemsOpen(true)}
                >
                  📋 {t('templates.bulk_import_items_btn')}
                </button>
              </div>
            ) : null}

            {section && editable ? (
              <BulkItemModal
                sectionLabel={section.label || section.key}
                existingItems={section.items}
                isOpen={bulkItemsOpen}
                onClose={() => setBulkItemsOpen(false)}
                onApply={(newItems) => updateSection({ items: newItems })}
              />
            ) : null}
          </section>
        )}

        {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

        {editable ? (
          <div className="tpl-ed__actions">
            <button
              type="button"
              className="tpl-ed__btn"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? t('templates.saving') : t('templates.save_draft')}
            </button>
            <span className="tpl-ed__fine">{t('templates.saved_draft_fine')}</span>
          </div>
        ) : null}
      </div>

      {/* aside */}
      <aside className="tpl-ed__side">
        <section className="tpl-ed__card tpl-ed__card--warm">
          <p className="tpl-ed__eyebrow">{t('templates.cannot_do_eyebrow')}</p>
          <p className="tpl-ed__body">{t('templates.cannot_do_body_1')}</p>
          <p className="tpl-ed__body tpl-ed__body--strong">{t('templates.cannot_do_body_2')}</p>
          <p className="tpl-ed__fine">{t('templates.cannot_do_fine')}</p>
        </section>

        <section className="tpl-ed__card">
          <p className="tpl-ed__eyebrow">{t('templates.inheritance')}</p>
          <p className="tpl-ed__body">
            {detail.parent_template_id
              ? t('templates.inherits_specializes_body')
              : t('templates.stands_on_own_body')}
          </p>
          <p className="tpl-ed__fine">{t('templates.inheritance_fine')}</p>
        </section>

        <section className="tpl-ed__card">
          <p className="tpl-ed__eyebrow">{t('templates.col_visibility')}</p>
          <p className="tpl-ed__body">
            {detail.visibility === 'public'
              ? t('templates.visibility_public_body')
              : detail.visibility === 'organization'
                ? t('templates.visibility_org_body')
                : t('templates.visibility_private_body')}
          </p>
          <p className="tpl-ed__fine">{t('templates.visibility_fine')}</p>
        </section>
      </aside>
    </div>
  );
}

/** Option sets, for the types that have them. Nothing is shown for the types that do not. */
function OptionEditor({
  item,
  editable,
  onChange,
}: {
  item: TemplateItem;
  editable: boolean;
  onChange: (options: TemplateOption[]) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation('common');
  const [bulkOpen, setBulkOpen] = useState(false);

  const bearsOptions =
    item.item_type === 'single_option' ||
    item.item_type === 'multi_option' ||
    item.item_type === 'scripture_ref' ||
    item.item_type === 'music_ref';

  if (!bearsOptions) return null;

  const options = item.options ?? [];

  return (
    <div className="tpl-ed__options">
      <p className="tpl-ed__hint">{t('templates.permitted_set_hint')}</p>

      {options.map((option, index) => (
        <div key={option.key} className="tpl-ed__option">
          <input
            className="tpl-ed__mono"
            value={option.key}
            disabled={!editable}
            onChange={(e) => {
              const next = [...options];
              next[index] = { ...option, key: e.target.value };
              onChange(next);
            }}
          />
          <input
            value={option.label}
            disabled={!editable}
            onChange={(e) => {
              const next = [...options];
              next[index] = { ...option, label: e.target.value };
              onChange(next);
            }}
          />
          {editable ? (
            <button
              type="button"
              className="tpl-ed__ghost"
              onClick={() => onChange(options.filter((_, i) => i !== index))}
            >
              ×
            </button>
          ) : (
            <span />
          )}
        </div>
      ))}

      {editable ? (
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
          <button
            type="button"
            className="tpl-ed__ghost"
            onClick={() =>
              onChange([
                ...options,
                {
                  key: `option_${options.length + 1}`,
                  label: t('templates.new_option_default'),
                  ordinal: options.length + 1,
                },
              ])
            }
          >
            + {t('templates.add_option')}
          </button>
          <button type="button" className="tpl-ed__ghost" onClick={() => setBulkOpen(true)}>
            📋 {t('templates.bulk_import_options_btn')}
          </button>
        </div>
      ) : null}

      <BulkOptionModal
        itemLabel={item.label}
        existingOptions={options}
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onApply={(newOptions) => onChange(newOptions)}
      />
    </div>
  );
}

// ── constraints ─────────────────────────────────────────────────────────────

/**
 * What must be settled before finalizing.
 *
 * Its own pane rather than a checkbox in the item table, because a constraint is a different kind of
 * statement from a label: it names an **authority** and gives an **explanation** that somebody will
 * read at the moment they are blocked. The mockup separates them for that reason, and the separation
 * is what makes the explanation feel like a required field rather than an afterthought.
 */
function ConstraintsPane({
  draft,
  version,
  templateId,
  onSaved,
}: {
  draft: TemplateVersionView | undefined;
  version: TemplateVersionView | undefined;
  templateId: string;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const source = draft?.definition ?? version?.definition ?? EMPTY;
  const [definition, setDefinition] = useState<TemplateDefinition>(source);

  useEffect(() => setDefinition(source), [draft?.id, source]);

  const save = useMutation({
    mutationFn: () => saveDraft(templateId, definition),
    onSuccess: onSaved,
  });

  const editable = draft !== undefined;
  const error = save.error instanceof Error ? (save.error as ApiError) : undefined;

  const rows = definition.sections.flatMap((section) =>
    section.items.map((item) => ({ section, item })),
  );

  const setConstraint = (
    sectionKey: string,
    itemKey: string,
    constraint: TemplateItem['constraint'],
  ): void => {
    setDefinition({
      ...definition,
      sections: definition.sections.map((section) =>
        section.key !== sectionKey
          ? section
          : {
              ...section,
              items: section.items.map((item) =>
                item.key !== itemKey
                  ? item
                  : constraint
                    ? { ...item, constraint }
                    : // Removed rather than set to a falsy value: presence *is* the flag.
                      (({ constraint: _dropped, ...rest }) => rest)(item),
              ),
            },
      ),
    });
  };

  return (
    <div className="tpl-ed__two">
      <div className="tpl-ed__main">
        <section className="tpl-ed__card">
          <p className="tpl-ed__eyebrow">{t('templates.blocking_finalization')}</p>
          <p className="tpl-ed__fine">{t('templates.constraints_fine')}</p>

          <ul className="tpl-ed__constraints">
            {rows.map(({ section, item }) => (
              <li key={`${section.key}:${item.key}`} className="tpl-ed__constraint">
                <div className="tpl-ed__constraint-head">
                  <label className="tpl-ed__check">
                    <input
                      type="checkbox"
                      checked={item.constraint !== undefined}
                      disabled={!editable}
                      onChange={(e) =>
                        setConstraint(
                          section.key,
                          item.key,
                          e.target.checked
                            ? {
                                kind: 'required_field',
                                source: t('templates.this_template'),
                                explanation: '',
                              }
                            : undefined,
                        )
                      }
                    />
                    <span>
                      <span className="tpl-ed__constraint-item">{item.label}</span>
                      <span className="tpl-ed__constraint-section">{section.label}</span>
                    </span>
                  </label>
                </div>

                {item.constraint ? (
                  <div className="tpl-ed__constraint-body">
                    <label className="tpl-ed__field">
                      <span className="tpl-ed__field-label">{t('templates.col_kind')}</span>
                      <select
                        value={item.constraint.kind}
                        disabled={!editable}
                        onChange={(e) =>
                          setConstraint(section.key, item.key, {
                            ...item.constraint!,
                            kind: e.target.value as ConstraintKind,
                          })
                        }
                      >
                        {CONSTRAINT_KINDS.map((kind) => (
                          <option key={kind} value={kind}>
                            {t(`templates.constraint_${kind}`, {
                              defaultValue: kind.replace(/_/g, ' '),
                            })}
                          </option>
                        ))}
                      </select>
                      <span className="tpl-ed__hint">
                        {/* The distinction that decides whether a parish may relax it later. */}
                        {t('templates.constraint_distinction_hint')}
                      </span>
                    </label>

                    <label className="tpl-ed__field">
                      <span className="tpl-ed__field-label">{t('templates.col_authority')}</span>
                      <input
                        value={item.constraint.source}
                        disabled={!editable}
                        placeholder="Order of Celebrating Matrimony"
                        onChange={(e) =>
                          setConstraint(section.key, item.key, {
                            ...item.constraint!,
                            source: e.target.value,
                          })
                        }
                      />
                      <span className="tpl-ed__hint">
                        {t('templates.constraint_authority_hint')}
                      </span>
                    </label>

                    <label className="tpl-ed__field">
                      <span className="tpl-ed__field-label">{t('templates.col_explanation')}</span>
                      <textarea
                        rows={2}
                        value={item.constraint.explanation}
                        disabled={!editable}
                        placeholder="A reading from the Old Testament is required."
                        onChange={(e) =>
                          setConstraint(section.key, item.key, {
                            ...item.constraint!,
                            explanation: e.target.value,
                          })
                        }
                      />
                      <span className="tpl-ed__hint">
                        {t('templates.constraint_explanation_hint')}
                      </span>
                    </label>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>

          {rows.length === 0 ? (
            <p className="tpl-ed__body">{t('templates.no_items_constrain')}</p>
          ) : null}

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          {editable ? (
            <div className="tpl-ed__actions">
              <button
                type="button"
                className="tpl-ed__btn"
                disabled={save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? t('templates.saving') : t('templates.save_draft')}
              </button>
            </div>
          ) : null}
        </section>
      </div>

      <aside className="tpl-ed__side">
        <section className="tpl-ed__card tpl-ed__card--warm">
          <p className="tpl-ed__eyebrow">{t('templates.roles_constrained')}</p>
          <p className="tpl-ed__body">{t('templates.roles_constrained_body')}</p>
          <p className="tpl-ed__fine">{t('templates.roles_constrained_fine')}</p>
        </section>
      </aside>
    </div>
  );
}

// ── localization ────────────────────────────────────────────────────────────

/**
 * Presentation only.
 *
 * A translation overrides labels and nothing else — structure, defaults and constraints are shared
 * across every language, because a Spanish reader and an English reader are planning the same
 * celebration. That is why this is a separate pane rather than a second column in the item table.
 *
 * Editable on a **published** version, unlike the definition: correcting a mistranslation changes
 * nothing any Liturgy planned, and freezing translations would mean living with a typo forever.
 */
function LocalizationPane({
  templateId,
  version,
}: {
  templateId: string;
  version: TemplateVersionView | undefined;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [language, setLanguage] = useState('es');
  const [messages, setMessages] = useState<Record<string, string>>({});

  const versionId = version?.id;

  const existing = useQuery({
    queryKey: ['template-localizations', templateId, versionId],
    queryFn: () => fetchLocalizations(templateId, versionId as string),
    enabled: versionId !== undefined,
  });

  useEffect(() => {
    const row = existing.data?.data.find((r) => r.language_code === language);
    setMessages(row?.messages ?? {});
  }, [existing.data, language]);

  const save = useMutation({
    mutationFn: () => saveLocalization(templateId, versionId as string, language, messages),
  });

  const keys = useMemo<{ key: string; source: string; kind: string }[]>(() => {
    if (!version) return [];

    const out: { key: string; source: string; kind: string }[] = [];
    for (const section of version.definition.sections) {
      out.push({ key: section.key, source: section.label, kind: 'Section' });
      for (const item of section.items) {
        out.push({ key: item.key, source: item.label, kind: 'Item' });
        for (const option of item.options ?? []) {
          out.push({ key: option.key, source: option.label, kind: 'Option' });
        }
      }
    }
    for (const role of version.definition.roles) {
      out.push({ key: role.key, source: role.label, kind: 'Role' });
    }
    return out;
  }, [version]);

  const error = save.error instanceof Error ? (save.error as ApiError) : undefined;

  if (!version) {
    return (
      <section className="tpl-ed__card">
        <p className="tpl-ed__body">{t('templates.no_version_translate')}</p>
      </section>
    );
  }

  return (
    <div className="tpl-ed__two">
      <div className="tpl-ed__main">
        <section className="tpl-ed__card">
          <div className="tpl-ed__card-head">
            <p className="tpl-ed__eyebrow">{t('templates.translations')}</p>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="es">{t('templates.espanol')}</option>
              <option value="en">{t('templates.english')}</option>
            </select>
          </div>

          <p className="tpl-ed__fine">{t('templates.localization_fine')}</p>

          <div className="tpl-ed__translations">
            {keys.map((entry) => (
              <div key={`${entry.kind}:${entry.key}`} className="tpl-ed__translation">
                <div className="tpl-ed__translation-source">
                  <span className="tpl-ed__translation-kind">{entry.kind}</span>
                  <span>{entry.source}</span>
                </div>
                <input
                  value={messages[entry.key] ?? ''}
                  placeholder={entry.source}
                  onChange={(e) => setMessages({ ...messages, [entry.key]: e.target.value })}
                />
              </div>
            ))}
          </div>

          {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

          <div className="tpl-ed__actions">
            <button
              type="button"
              className="tpl-ed__btn"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? t('templates.saving') : t('templates.save_translations')}
            </button>
            {save.isSuccess ? <span className="tpl-ed__fine">{t('templates.saved')}</span> : null}
          </div>
        </section>
      </div>

      <aside className="tpl-ed__side">
        <section className="tpl-ed__card tpl-ed__card--warm">
          <p className="tpl-ed__eyebrow">{t('templates.presentation_only')}</p>
          <p className="tpl-ed__body">{t('templates.presentation_body')}</p>
          <p className="tpl-ed__fine">{t('templates.presentation_fine')}</p>
        </section>
      </aside>
    </div>
  );
}

// ── versions ────────────────────────────────────────────────────────────────

function VersionsPane({ detail }: { detail: TemplateDetail }): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <div className="tpl-ed__two">
      <div className="tpl-ed__main">
        <section className="tpl-ed__card">
          <p className="tpl-ed__eyebrow">{t('templates.versions')}</p>
          <ul className="tpl-ed__versions">
            {[...detail.versions].reverse().map((version) => {
              const sectionsCount = version.definition.sections.length;
              const itemsCount = version.definition.sections.reduce(
                (n, s) => n + s.items.length,
                0,
              );
              return (
                <li key={version.id} className="tpl-ed__version">
                  <div>
                    <div className="tpl-ed__version-name">v{version.version_number}</div>
                    <div className="tpl-ed__version-meta">
                      {version.published_at
                        ? t('templates.published_date', {
                            date: version.published_at.slice(0, 10),
                          })
                        : t('templates.draft_not_published')}
                      {' · '}
                      {t('templates.sections_count', { count: sectionsCount })},{' '}
                      {t('templates.items_count', { count: itemsCount })}
                    </div>
                  </div>
                  <span
                    className={
                      version.is_published ? 'tpl-ed-pill tpl-ed-pill--published' : 'tpl-ed-pill'
                    }
                  >
                    {version.is_published ? t('templates.version_frozen') : t('templates.draft')}
                  </span>
                </li>
              );
            })}
          </ul>

          {detail.versions.length === 0 ? (
            <p className="tpl-ed__body">{t('templates.nothing_drafted')}</p>
          ) : null}
        </section>
      </div>

      <aside className="tpl-ed__side">
        <section className="tpl-ed__card tpl-ed__card--warm">
          <p className="tpl-ed__eyebrow">{t('templates.why_frozen')}</p>
          <p className="tpl-ed__body">{t('templates.why_frozen_body_1')}</p>
          <p className="tpl-ed__body tpl-ed__body--strong">{t('templates.why_frozen_body_2')}</p>
        </section>
      </aside>
    </div>
  );
}

// ── publishing ──────────────────────────────────────────────────────────────

/**
 * The mockup's modal, titled *"Existing liturgies will not change"*.
 *
 * It leads with the count of celebrations pinned to each published version, because stating the
 * guarantee is weaker than showing what it protects. A publisher who can see the eighteen
 * celebrations that keep v3 believes the sentence above them.
 */
function PublishModal({
  templateId,
  versionNumber,
  onClose,
  onPublished,
}: {
  templateId: string;
  versionNumber: number;
  onClose: () => void;
  onPublished: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const preview = useQuery({
    queryKey: ['publish-preview', templateId],
    queryFn: () => fetchPublishPreview(templateId),
  });

  const publish = useMutation({
    mutationFn: () => publishDraft(templateId),
    onSuccess: onPublished,
  });

  const error = publish.error instanceof Error ? (publish.error as ApiError) : undefined;
  const pinned = preview.data?.pinned ?? [];
  const total = pinned.reduce((n, row) => n + row.liturgy_count, 0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="tpl-ed__overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="tpl-ed__modal">
        <h2 className="tpl-ed__modal-title">{t('templates.existing_will_not_change')}</h2>

        <p className="tpl-ed__body">
          {t('templates.publish_modal_body', { version: versionNumber })}
        </p>

        {total > 0 ? (
          <ul className="tpl-ed__pinned">
            {pinned
              .filter((row) => row.liturgy_count > 0)
              .map((row) => (
                <li key={row.version_id}>
                  <strong>{row.liturgy_count}</strong>{' '}
                  {t('templates.celebration_keeps', {
                    count: row.liturgy_count,
                    version: row.version_number,
                  })}
                </li>
              ))}
          </ul>
        ) : (
          <p className="tpl-ed__fine">{t('templates.nothing_planned_yet')}</p>
        )}

        <p className="tpl-ed__fine">{t('templates.adopting_newer_fine')}</p>

        {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

        <div className="tpl-ed__actions">
          <button
            type="button"
            className="tpl-ed__btn"
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
          >
            {publish.isPending
              ? t('templates.publishing')
              : t('templates.publish_v', { version: versionNumber })}
          </button>
          <button type="button" className="tpl-ed__ghost" onClick={onClose}>
            {t('templates.not_yet')}
          </button>
        </div>
      </div>
    </div>
  );
}
